import cx from 'classnames';
import { Link } from '@dx-ui/osc-link';
import { Icon } from '@dx-ui/osc-icon';
import type { OSCIconNames } from '@dx-ui/osc-icon';

const sizeMap = {
  xs: 'btn--xs',
  sm: 'btn--sm',
  md: 'btn--md',
  lg: 'btn--lg',
} as const;

const colorMap = {
  brand: 'btn--brand',
  accent: 'btn--accent',
  enterprise: 'btn--enterprise',
  success: 'btn--success',
  inherit: '',
} as const;

const fillMap = {
  auto: 'btn--auto',
  solid: 'btn--solid',
  outline: 'btn--outline',
  flat: 'btn--flat',
} as const;

export type ButtonFill = keyof typeof fillMap;
export type ButtonSize = keyof typeof sizeMap;
export type ButtonColor = keyof typeof colorMap;

type IconProps = {
  name: OSCIconNames;
  align?: 'before' | 'after';
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'regular' | 'solid';
  active?: boolean;
  spacing?: '0' | '1' | '2';
};

type CommonButtonProps = {
  /** Optional screen reader only label to provide more context to the user */
  accessibleLabel?: React.ReactNode;
  /** Optional classname to pass to the Button */
  className?: string;
  /** Button color */
  color?: ButtonColor;
  /** Button fill */
  fill?: ButtonFill;
  /* Optional prop to apply inline text styles and disable Uppercase text from being set on buttons */
  textStyle?: 'inline-text-style' | 'block-text-style';
  /** Add Icon props to buttons with Icons. Can be a single icon prop config or an array of icon prop configs. */
  iconProps?: IconProps | IconProps[];
  /** Button label */
  label: React.ReactNode;
  /** Button size */
  size?: ButtonSize;
  /** Display only the icon, keeping label accessible to screen readers */
  iconOnly?: boolean;
  /** Whether or not the link should be underlined */
  underline?: boolean;
};

type ButtonProps = CommonButtonProps &
  Omit<React.ComponentPropsWithRef<'button'>, keyof CommonButtonProps> & {
    as?: 'button';
    /** Button type */
    type?: 'button' | 'submit';
    url?: never;
    showNewWindowIcon?: never;
    isNewWindow?: never;
  };

export type LinkProps = CommonButtonProps &
  Omit<React.ComponentPropsWithoutRef<typeof Link>, 'label' | keyof CommonButtonProps> & {
    as: 'Link';
    type?: never;
  };

export type ButtonComponentProps = ButtonProps | LinkProps;

const baseButtonClasses = 'btn--base';

function getIconSpacingClass({
  iconAlign,
  size,
  iconOnly,
  spacingOverride,
}: {
  iconAlign: 'before' | 'after';
  size: ButtonSize;
  iconOnly: boolean;
  spacingOverride?: '0' | '1' | '2';
}) {
  if (iconOnly) return undefined;

  const spacing = spacingOverride ?? (size === 'sm' || size === 'xs' ? '1' : '2');
  return iconAlign === 'before' ? `me-${spacing}` : `ms-${spacing}`;
}

export function getButtonClasses(fill: ButtonFill, size: ButtonSize, color: ButtonColor) {
  return cx(baseButtonClasses, fillMap[fill], sizeMap[size], colorMap[color]);
}

export function Button(props: ButtonComponentProps) {
  const {
    accessibleLabel,
    as,
    className,
    color = 'brand',
    fill = 'auto',
    textStyle = 'block-text-style',
    iconProps,
    label,
    size = 'md',
    type = 'button',
    iconOnly = false,
    underline = true,
    ...rest
  } = props;
  const element = as || 'button';
  const sharedClassName = getButtonClasses(fill, size, color);

  const linkAriaLabel =
    as === 'Link' && typeof accessibleLabel === 'string' && accessibleLabel
      ? accessibleLabel
      : undefined;
  let iconArray: IconProps[] = [];
  if (Array.isArray(iconProps)) {
    iconArray = iconProps;
  } else if (iconProps) {
    iconArray = [iconProps];
  }
  const iconAlign = iconArray[0]?.align || 'before';
  const shouldDisplayIcon = iconArray.length > 0;
  const iconSpacingClass = shouldDisplayIcon
    ? getIconSpacingClass({ iconAlign, size, iconOnly, spacingOverride: iconArray[0]?.spacing })
    : undefined;

  const children = (
    <>
      {shouldDisplayIcon ? (
        <span className={iconSpacingClass}>
          {iconArray.map((icon) => (
            <Icon
              key={icon?.name}
              variant={icon.variant}
              name={icon.name}
              size={icon.size}
              className={icon.className}
            />
          ))}
        </span>
      ) : null}
      {accessibleLabel && !linkAriaLabel ? (
        <>
          <span className="sr-only">{accessibleLabel}</span>
          <span aria-hidden="true">{label}</span>
        </>
      ) : (
        <span className={iconOnly ? 'sr-only' : undefined}>{label}</span>
      )}
    </>
  );

  if (element === 'button') {
    const buttonProps = rest as React.ComponentProps<'button'>;

    return (
      <button
        type={type === 'submit' ? 'submit' : 'button'}
        {...buttonProps}
        className={cx(className, sharedClassName, {
          'flex items-center justify-center': shouldDisplayIcon,
          'flex-row-reverse': iconAlign === 'after',
          'inline-text-style': textStyle === 'inline-text-style',
          'btn--icon-only': iconOnly,
          'btn--no-underline': !underline,
          'text-current': color === 'inherit' && fill === 'flat',
        })}
      >
        {children}
      </button>
    );
  }

  const linkProps = rest as unknown as React.ComponentProps<typeof Link>;

  return (
    <Link
      {...linkProps}
      {...(linkAriaLabel ? { 'aria-label': linkAriaLabel } : {})}
      className={cx('text-current', {
        'flex items-center justify-center': shouldDisplayIcon,
        'flex-row-reverse': iconAlign === 'after',
      })}
      anchorClassName={cx(sharedClassName, className, 'inline-block text-center', {
        'btn--icon-only': iconOnly,
        'btn--no-underline': !underline,
      })}
      underline={linkProps.underline ?? false}
    >
      {children}
    </Link>
  );
}
